import { Dialog, DialogContent, DialogTitle, IconButton, Skeleton, Typography } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { GridColDef, GridRenderCellParams, GridSortModel } from "@mui/x-data-grid";
import { useAtom } from "jotai";
import { useEffect, useMemo, useRef, useState } from "react";
import { GridColumnVisibility } from "../../components/cards/RelevantTextRecordsCard/state/GridColumnVisibility";
import {
  ExpandedRowsAtom,
  VerbatimColumns,
} from "../../components/cards/RelevantTextRecordsCard/state/VerbatimColumns";
import { StyledDataGrid } from "../../components/common";
import { useVerbatims } from "../../contexts/VerbatimsProvider";
import { SearchResults, SortDirEnum } from "../../models/search";

const NUM_SKELETON_ROWS = 8;

const buildSkeletonGridRows = (columns: GridColDef[]): GridColDef[] =>
  columns.map((col) => ({
    ...col,
    renderCell: () => <Skeleton variant="text" width={col.width ?? 200} />,
  }));

const TruncatedCellWithModal = ({ value, onView }: { value: string; onView: () => void }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setIsTruncated(el.scrollWidth > el.clientWidth);
  }, [value]);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        width: "100%",
      }}
    >
      <div
        ref={ref}
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          flex: 1,
        }}
        title={isTruncated ? value : undefined}
      >
        {value}
      </div>

      {isTruncated && (
        <IconButton size="small" onClick={onView} aria-label="View full text">
          <VisibilityIcon fontSize="small" />
        </IconButton>
      )}
    </div>
  );
};

const VerbatimsGrid = (): JSX.Element => {
  const { records, isLoading, sort, updateSort } = useVerbatims();

  const [columnVisibilityModel, setColumnVisibilityModel] = useAtom(GridColumnVisibility);
  const [columns] = useAtom(VerbatimColumns);
  const [expandedRows] = useAtom(ExpandedRowsAtom);

  const [open, setOpen] = useState(false);
  const [modalText, setModalText] = useState("");
  const [modalTitle, setModalTitle] = useState("");

  const sortKey = sort ? (Object.keys(sort)[0] as keyof SearchResults) : null;

  const currentSortModel: GridSortModel | undefined =
    sortKey && sort ? [{ field: sortKey, sort: sort[sortKey] }] : undefined;

  const handleSortChange = (newSortModel: GridSortModel) => {
    if (!newSortModel.length && sortKey) {
      updateSort(sortKey, null);
    } else {
      updateSort(
        newSortModel[0].field as keyof SearchResults,
        newSortModel[0].sort ? SortDirEnum[newSortModel[0].sort] : null,
      );
    }
  };

  // Insert each expanded parent's child records directly beneath it. Children are
  // tagged with `_parent_id` so the "Related" column renders the child indicator.
  const displayRows = useMemo(() => {
    const source = records ?? [];
    return source.flatMap((record, i) => {
      const parentId = String(record._id ?? i);
      const parentRow = { ...record, id: parentId };
      if (!expandedRows.includes(record._id) || !record.children?.length) {
        return [parentRow];
      }
      const childRows = record.children.map((child, ci) => ({
        ...child,
        _parent_id: record._id,
        id: `${parentId}::child::${String(child._id ?? ci)}`,
      }));
      return [parentRow, ...childRows];
    });
  }, [records, expandedRows]);

  const displayColumns: GridColDef[] = columns
    .map((col) =>
      col.renderCell
        ? { ...col }
        : {
            ...col,
            renderCell: (params: GridRenderCellParams) => (
              <TruncatedCellWithModal
                value={String(params.value ?? "")}
                onView={() => {
                  setModalText(String(params.value ?? ""));
                  setModalTitle(col.headerName ?? "Details");
                  setOpen(true);
                }}
              />
            ),
          },
    )
    .map((col) => (col.width ? col : { ...col, width: 200 }));

  return (
    <>
      <StyledDataGrid
        sx={{
          height: "calc(100vh - 252px)",
        }}
        rows={
          !isLoading
            ? displayRows
            : [...Array(NUM_SKELETON_ROWS)].map((_, i) => ({
                id: String(i),
              }))
        }
        columns={!isLoading ? displayColumns : buildSkeletonGridRows(displayColumns)}
        hideFooter
        hideFooterPagination
        disableSelectionOnClick
        disableColumnFilter
        paginationMode="server"
        rowCount={(records ?? []).length}
        initialState={{
          sorting: {
            sortModel: currentSortModel,
          },
        }}
        onSortModelChange={handleSortChange}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
      />

      {/* -------- Expanded Text Modal -------- */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{modalTitle}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
            {modalText}
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VerbatimsGrid;
